/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import useUser from '../../../Shared/UserProvider/useUser'
import { useQuery } from '@tanstack/react-query'
import { getBusyModeDuration } from '../../../../services/PointOfSale/PointOfSale.service'
import { isProviderAPos } from '../../../../services/Provider/Provider.service'
import { Card, CardContent, CardExtra } from '../../Shared/Card'
import { Icon } from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import { useTranslation } from 'react-i18next'
import { LoadingPlaceholderCardContent } from '../../Shared/LoadingPlaceholderCardContent'
import { formatISO, startOfDay } from 'date-fns'


const BusyModeDurationCard = () => {
    const [t] = useTranslation()
    const {user} = useUser()

    const {data, isLoading} = useQuery({
        queryKey: ['busy-mode-duration', user.provider.object.id],
        queryFn: () => getBusyModeDuration(user.provider.object.id),
        enabled: isProviderAPos(user.provider),
    })

    const hasNoBusyMode = (duration) => {
        if (!duration) return true

        return (duration.years || 0) === 0 &&
          (duration.months || 0) === 0 &&
          (duration.days || 0) === 0 &&
          (duration.hours || 0) === 0 &&
          (duration.minutes || 0) === 0 &&
          (duration.seconds || 0) === 0
    }

    const formatBusyModeDuration = (duration) => {
        if (!duration) return t('dashboard.busy_mode_duration.no_busy_mode')

        if (hasNoBusyMode(duration)) return t('dashboard.busy_mode_duration.no_busy_mode')

        const totalMinutes = (duration.hours || 0) * 60 + (duration.minutes || 0) + Math.ceil((duration.seconds || 0) / 60)

        if (totalMinutes <= 2) {
            return t('dashboard.busy_mode_duration.minute_in_busy_mode', {count: totalMinutes})
        }

        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60

        if (hours === 0) {
            return t('dashboard.busy_mode_duration.minute_in_busy_mode', {count: minutes})
        }

        if (minutes === 0) {
            return t('dashboard.busy_mode_duration.hour_in_busy_mode', {hour: hours, minute: '00'})
        }

        return t('dashboard.busy_mode_duration.hour_in_busy_mode', {
            hour: hours,
            minute: minutes.toString().padStart(2, '0'),
        })
    }

    const noBusyMode = !isLoading && hasNoBusyMode(data)
    const hasBusyMode = !isLoading && !hasNoBusyMode(data)

    return (
      <Card
        css={css`
            ${noBusyMode && `
                border-top: 2px solid #21ba45;
            `}
            ${hasBusyMode && `
                border-top: 2px solid #f26202;
            `}

            .card-content {
                width: 225px;
                height: 62px;
            }
        `}
      >
          {isLoading ? (
            <LoadingPlaceholderCardContent/>
          ) : (
            <CardContent
              css={css`
                  display: flex;
                  justify-content: flex-start;
                  align-items: center;
                  background: transparent;
              `}
            >
                <div
                  css={css`
                      display: flex;
                      align-items: center;
                      column-gap: 1rem;
                      margin-left: 1rem;
                  `}
                >
                    {noBusyMode && <Icon name="checkmark" color="green" size="big"/>}
                    {hasBusyMode && <Icon name="warning" color="orange" size="big"/>}
                    <p
                      css={css`
                          font-weight: bold;
                          color: ${noBusyMode ? '#21ba45' : hasBusyMode ? '#f26202' : ''};
                      `}>
                        {formatBusyModeDuration(data)}
                    </p>
                </div>
            </CardContent>
          )}

          <CardExtra>
              <Link
                to={{
                    pathname: '/analytics/openings/opening-times',
                    search: new URLSearchParams({
                        from: formatISO(startOfDay(new Date())),
                        to: formatISO(new Date()),
                    }).toString(),
                }}>
                  {t('dashboard.opening.extra_link')}
                  <Icon name="arrow right"/>
              </Link>
          </CardExtra>
      </Card>
    )
}

export { BusyModeDurationCard }
